import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FolderRow from "./FolderRow";
import ProjectCard from "./ProjectCard";
import TrebbleInfiniteList from "../common/InfiniteList";
import TrebbleSkeleton from "../common/TrebbleSkeleton";
import TrebbleFilterButtons from "../common/TrebbleFilterButtons";
import LibrarySearchBar from "./LibrarySearchBar";
import LibraryToolbar from "./LibraryToolbar";
import LibraryFilterPopup from "./LibraryFilterPopup";
import LibrarySearchResults from "./LibrarySearchResults";
import TrebbleClientAPI from "../../models/helper/TrebbleClientAPI";
import LocalStorageHelper from "../../models/helper/LocalStorageHelper";
import ti18n from "i18n!nls/MyLibraryi18n";
import Utils from "../../models/helper/Utils";
import FolderNameModal from "../common/FolderNameModal";
import PersistentModels from "../../services/PersistentModels";
import "./styles.css";

interface UtilsSingleton {
  showUserConfirmationDialog: (title: string, message: string) => Promise<boolean>;
}

interface UtilsModule {
  getInstance: () => UtilsSingleton;
}

// Window globals merged with existing declarations across the codebase.
// `alertErrorMessage` is already declared as `(error: any) => void` elsewhere,
// and `getI18n` as `(i18nData: any, key: string, options?: any) => string`.
// Match those signatures to avoid TS2717 merge conflicts.
declare global {
  interface Window {
    alertSuccessMessage: (msg: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    alertErrorMessage: (error: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getI18n: (i18nData: any, key: string, options?: any) => string;
  }
}

type AssetType = "audio" | "video" | "image" | "vector";
type ViewMode = "grid" | "list";

type WorkspaceContextValue = {
  workspaceId?: string;
  workspaceName?: string;
  myRole?: string;
  isAdmin?: boolean;
  workspaceStatus?: string;
} | null;

interface Folder {
  id: string;
  name: string;
  color: string;
  itemCount: number;
  itemIds?: string[];
  parentFolderId?: string | null;
}

/** Collect a folder ID and all its descendant IDs (to prevent circular moves). */
function getFolderAndDescendantIds(folderId: string, folders: Folder[]): Set<string> {
  const ids = new Set<string>([folderId]);
  let added = true;
  while (added) {
    added = false;
    for (const f of folders) {
      if (f.parentFolderId && ids.has(f.parentFolderId) && !ids.has(f.id)) {
        ids.add(f.id);
        added = true;
      }
    }
  }
  return ids;
}

interface AssetCardData {
  id: string;
  name: string;
  type: AssetType;
  sourceType: "uploaded_media" | "editor_media_asset";
  ownerUserUID?: string | null;
  folderId?: string | null;
  url?: string;
  audioUrl?: string;
  videoUrl?: string;
  previewUrl?: string;
  duration?: string;
  date: string;
  createdTimestamp: number;
  creator?: {
    name: string;
    initials: string;
    color: string;
  };
}

type AssetResponseRecord = Record<string, unknown>;

const PAGE_SIZE = 30;
const WORKSPACE_ASSET_FILTER_ALL = "all";
const WORKSPACE_ASSET_FILTER_OWNED = "ownedByMe";
const FOLDER_COLORS = [
  "#a8e21d",
  "#c5a3fb",
  "#ffa7d4",
  "#82d9e3",
  "#70A098",
  "#4A90D9",
];

const SORT_OPTIONS: Array<{ key: string; labelKey: string }> = [
  { key: "newest", labelKey: "SORT_NEWEST_FIRST" },
  { key: "oldest", labelKey: "SORT_OLDEST_FIRST" },
  { key: "name_asc", labelKey: "SORT_NAME_ASC" },
  { key: "name_desc", labelKey: "SORT_NAME_DESC" },
];

const t = (key: string): string => {
  return window.getI18n(ti18n, key);
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      minHeight: 220,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px 0",
      textAlign: "center",
      color: "var(--text-muted, #888)",
      fontSize: "var(--text-base, 14px)",
    }}
  >
    {message}
  </div>
);

const extractFolderRecords = (response: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(response)) {
    return response as Array<Record<string, unknown>>;
  }

  if (response && typeof response === "object") {
    const responseObject = response as {
      folders?: Array<Record<string, unknown>>;
      items?: Array<Record<string, unknown>>;
      message?: Array<Record<string, unknown>>;
      _id?: unknown;
      id?: unknown;
    };

    if (Array.isArray(responseObject.folders)) {
      return responseObject.folders;
    }

    if (Array.isArray(responseObject.items)) {
      return responseObject.items;
    }

    if (Array.isArray(responseObject.message)) {
      return responseObject.message;
    }

    if (responseObject._id || responseObject.id) {
      return [response as Record<string, unknown>];
    }
  }

  return [];
};

const mapFolderRecord = (folder: Record<string, unknown>, index: number): Folder => {
  const itemIds = Array.isArray(folder.itemIds)
    ? folder.itemIds
        .map((itemId) => (itemId ? itemId.toString() : ""))
        .filter(Boolean)
    : [];

  return {
    id: ((folder._id || folder.id || "") as string),
    name: ((folder.name || "Untitled Folder") as string),
    color: ((folder.color || FOLDER_COLORS[index % FOLDER_COLORS.length]) as string),
    itemCount:
      typeof folder.itemCount === "number" ? (folder.itemCount as number) : itemIds.length,
    itemIds,
    parentFolderId: folder.parentFolderId ? String(folder.parentFolderId) : null,
  };
};

const isWorkspaceFeatureEnabled = (): boolean => {
  try {
    const rollout = (window as unknown as Record<string, unknown>).FeatureRolloutHelper as
      { isFeatureEnabled?: (feature: unknown) => boolean; FEATURES?: Record<string, unknown> } | undefined;
    if (rollout && typeof rollout.isFeatureEnabled === "function" && rollout.FEATURES) {
      return rollout.isFeatureEnabled(rollout.FEATURES.WORKSPACE_ENABLED);
    }
    return false;
  } catch {
    return false;
  }
};

const getStoredWorkspaceContext = (): WorkspaceContextValue => {
  try {
    if (!isWorkspaceFeatureEnabled()) {
      return null;
    }
    return LocalStorageHelper.getInstance().getWorkspaceContext() as WorkspaceContextValue;
  } catch {
    return null;
  }
};

const normalizeAssetListResponse = (response: unknown): AssetResponseRecord[] => {
  if (Array.isArray(response)) {
    return response as AssetResponseRecord[];
  }

  if (response && typeof response === "object") {
    const responseObject = response as {
      items?: AssetResponseRecord[];
      assets?: AssetResponseRecord[];
      message?: AssetResponseRecord[];
    };

    if (Array.isArray(responseObject.items)) {
      return responseObject.items;
    }

    if (Array.isArray(responseObject.assets)) {
      return responseObject.assets;
    }

    if (Array.isArray(responseObject.message)) {
      return responseObject.message;
    }
  }

  return [];
};

const getColorFromSeed = (seed: string): string => {
  const colors = ["#a8e21d", "#c5a3fb", "#ffa7d4", "#82d9e3", "#70A098", "#4A90D9"];
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = ((hash << 5) - hash + seed.charCodeAt(index)) | 0;
  }
  return colors[Math.abs(hash) % colors.length];
};

const buildInitials = (name: string): string => {
  return (
    name
      .split(" ")
      .map((token) => token[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?"
  );
};

const formatAssetDate = (value: unknown): { label: string; timestamp: number } => {
  const date = value ? new Date(value as string | number | Date) : null;
  const timestamp = date ? date.getTime() : 0;

  if (!date || Number.isNaN(timestamp)) {
    return { label: "", timestamp: 0 };
  }

  return {
    label: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    timestamp,
  };
};

const formatDuration = (value: unknown): string | undefined => {
  const totalSeconds =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : 0;

  if (!totalSeconds || Number.isNaN(totalSeconds) || totalSeconds <= 0) {
    return undefined;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const sortAssets = (assets: AssetCardData[], activeSort: string): AssetCardData[] => {
  const sortedAssets = [...assets];

  if (activeSort === "oldest") {
    sortedAssets.sort((assetA, assetB) => assetA.createdTimestamp - assetB.createdTimestamp);
  } else if (activeSort === "name_asc") {
    sortedAssets.sort((assetA, assetB) => assetA.name.localeCompare(assetB.name));
  } else if (activeSort === "name_desc") {
    sortedAssets.sort((assetA, assetB) => assetB.name.localeCompare(assetA.name));
  } else {
    sortedAssets.sort((assetA, assetB) => assetB.createdTimestamp - assetA.createdTimestamp);
  }

  return sortedAssets;
};

const buildAssetCard = (asset: AssetResponseRecord): AssetCardData => {
  const assetId = (asset.assetId || asset._id || asset.id || "") as string;
  const assetType = ((asset.assetType || "audio") as AssetType);
  const createdDateInfo = formatAssetDate(asset.createdDate || asset.creationDate);
  const ownerFullName = ((asset.ownerName || asset.ownerUsername || "Unknown") as string).trim();
  // Use first name only for consistency with project cards
  const ownerName = ownerFullName.split(" ")[0] || ownerFullName;
  const ownerSeed = ((asset.ownerUsername || ownerName || assetId) as string);

  return {
    id: assetId,
    name: ((asset.name || "Untitled") as string),
    type: assetType,
    sourceType: ((asset.sourceType || "uploaded_media") as "uploaded_media" | "editor_media_asset"),
    ownerUserUID: (asset.ownerUserUID as string | null | undefined) || null,
    folderId: (asset.folderId as string | null | undefined) || null,
    url: (asset.url as string | undefined) || undefined,
    audioUrl: (asset.audioUrl as string | undefined) || (assetType === "audio" ? (asset.url as string | undefined) : undefined),
    videoUrl: (asset.videoUrl as string | undefined) || (assetType === "video" ? (asset.url as string | undefined) : undefined),
    previewUrl:
      (asset.previewUrl as string | undefined) ||
      ((assetType === "image" || assetType === "vector") ? (asset.url as string | undefined) : undefined),
    duration: formatDuration(asset.durationInSeconds),
    date: createdDateInfo.label,
    createdTimestamp: createdDateInfo.timestamp,
    creator: ownerName
      ? {
          name: ownerName,
          initials: buildInitials(ownerFullName),
          color: getColorFromSeed(ownerSeed),
        }
      : undefined,
  };
};

const AssetsLibraryPage: React.FC = () => {
  const [workspaceContext, setWorkspaceContext] = useState<WorkspaceContextValue>(() => getStoredWorkspaceContext());
  const currentWorkspaceId = workspaceContext?.workspaceId || null;
  const currentUserId = (() => {
    try {
      return LocalStorageHelper.getInstance().getUserInfo()?._id || null;
    } catch {
      return null;
    }
  })();

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [workspaceAssetFilter, setWorkspaceAssetFilter] = useState(WORKSPACE_ASSET_FILTER_ALL);
  const [activeSort, setActiveSort] = useState("newest");
  const [filterOpen, setFilterOpen] = useState(false);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [activeFolder, setActiveFolder] = useState<Folder | null>(null);
  const [folderPath, setFolderPath] = useState<Folder[]>([]);
  const [folderMutationTick, setFolderMutationTick] = useState(0);
  const [cachedAssets, setCachedAssets] = useState<AssetCardData[]>([]);
  const [allCachedAssets, setAllCachedAssets] = useState<AssetCardData[]>([]);
  const [searchResults, setSearchResults] = useState<AssetCardData[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchResultsView, setIsSearchResultsView] = useState(false);
  // searchDebounceRef removed — search is submit-only (Google Drive pattern)

  const [folderNameModalOpen, setFolderNameModalOpen] = useState(false);
  const [pendingMoveAsset, setPendingMoveAsset] = useState<AssetCardData | null>(null);
  const [renameFolderModalOpen, setRenameFolderModalOpen] = useState(false);
  const [pendingRenameFolder, setPendingRenameFolder] = useState<Folder | null>(null);
  const [pendingMoveFolder, setPendingMoveFolder] = useState<Folder | null>(null);

  // Folder management is allowed for any workspace member (membership check is
  // enforced server-side). For the personal/no-workspace context, there is no
  // workspace to gate, so we still allow management. The denial branches below
  // remain as a defensive fallback should a future role be added that should
  // not have management rights.
  const canManageFolders = useMemo(() => {
    if (!workspaceContext) return true;
    // If a future workspace status disables management, gate it here.
    return workspaceContext.workspaceStatus !== "READ_ONLY";
  }, [workspaceContext]);

  const rootLabel = t("ASSETS");

  useEffect(() => {
    const syncWorkspaceContext = () => {
      setWorkspaceContext(getStoredWorkspaceContext());
      setActiveFolder(null);
      setFolderPath([]);
    };

    syncWorkspaceContext();
    window.addEventListener("trebble:workspace-library-updated", syncWorkspaceContext);

    return () => {
      window.removeEventListener("trebble:workspace-library-updated", syncWorkspaceContext);
    };
  }, []);

  useEffect(() => {
    setSearchQuery("");
    setAppliedSearch("");
    setSearchResults([]);
    setIsSearchResultsView(false);
    setWorkspaceAssetFilter(WORKSPACE_ASSET_FILTER_ALL);
  }, [currentWorkspaceId]);

  // Debounce cleanup removed — search is submit-only

  const folderByAssetId = useMemo(() => {
    const result = new Map<string, Folder>();
    const sourceFolders = allFolders.length > 0 ? allFolders : folders;

    sourceFolders.forEach((folder) => {
      (folder.itemIds || []).forEach((itemId) => {
        result.set(itemId, folder);
      });
    });

    return result;
  }, [allFolders, folders]);

  const assetLookup = useMemo(() => {
    const result = new Map<string, AssetCardData>();

    cachedAssets.forEach((asset) => {
      result.set(asset.id, asset);
    });

    searchResults.forEach((asset) => {
      result.set(asset.id, asset);
    });

    return result;
  }, [cachedAssets, searchResults]);

  const normalizeFolderListResponse = useCallback((response: unknown): Folder[] => {
    return extractFolderRecords(response)
      .map((folder, index) => mapFolderRecord(folder, index))
      .filter((folder) => !!folder.id);
  }, []);

  const getListParams = useCallback(
    (params?: {
      searchKeyword?: string;
      pageOffset?: number;
      numberOfResults?: number;
      folderId?: string | null;
      assetType?: string | null;
    }) => ({
      workspaceId: currentWorkspaceId || undefined,
      searchKeyword: params?.searchKeyword || undefined,
      pageOffset: params?.pageOffset || 0,
      numberOfResults: params?.numberOfResults || PAGE_SIZE,
      folderId: params?.folderId || undefined,
      assetType: params?.assetType || undefined,
      unfiledOnly: !params?.folderId && !params?.searchKeyword ? "true" : undefined,
    }),
    [currentWorkspaceId]
  );

  const loadFolders = useCallback(async () => {
    try {
      const response = await TrebbleClientAPI.getInstance().listAssetFolders({
        workspaceId: currentWorkspaceId || undefined,
        parentFolderId: activeFolder?.id || null,
      });
      const nextFolders = normalizeFolderListResponse(response);
      setFolders(nextFolders);
    } catch (error) {
      console.error("Failed to load asset folders:", error);
      setFolders([]);
    }
  }, [activeFolder?.id, currentWorkspaceId, normalizeFolderListResponse]);

  const loadAllFolders = useCallback(async () => {
    try {
      const response = await TrebbleClientAPI.getInstance().listAssetFolders({
        workspaceId: currentWorkspaceId || undefined,
      });
      const normalized = normalizeFolderListResponse(response);
      setAllFolders(normalized);
      return normalized;
    } catch (err) {
      console.error("Failed to load all asset folders:", err);
      return [];
    }
  }, [currentWorkspaceId, normalizeFolderListResponse]);

  useEffect(() => {
    loadFolders();
    loadAllFolders();
  }, [loadFolders, loadAllFolders]);

  const matchesWorkspaceAssetFilter = useCallback(
    (asset: AssetCardData) => {
      if (!currentWorkspaceId || workspaceAssetFilter === WORKSPACE_ASSET_FILTER_ALL) {
        return true;
      }

      return !!(currentUserId && asset.ownerUserUID && asset.ownerUserUID === currentUserId);
    },
    [currentUserId, currentWorkspaceId, workspaceAssetFilter]
  );

  const fetchAssetsForContext = useCallback(
    async ({
      pageOffset,
      numberOfResults,
      searchKeyword,
      folderId,
      assetType,
    }: {
      pageOffset: number;
      numberOfResults: number;
      searchKeyword?: string;
      folderId?: string | null;
      assetType?: string | null;
    }): Promise<{ items: AssetCardData[]; totalCount: number; hasMore: boolean }> => {
      const response = await TrebbleClientAPI.getInstance().listAssets(
        getListParams({
          pageOffset,
          numberOfResults,
          searchKeyword,
          folderId,
          assetType,
        })
      );

      const items = normalizeAssetListResponse(response).map(buildAssetCard);
      const responseObject = response && typeof response === "object" ? (response as { totalCount?: number; hasMore?: boolean }) : {};

      return {
        items,
        totalCount: responseObject.totalCount || items.length,
        hasMore: typeof responseObject.hasMore === "boolean" ? responseObject.hasMore : items.length >= numberOfResults,
      };
    },
    [getListParams]
  );

  useEffect(() => {
    const loadCachedAssets = async () => {
      try {
        const response = await fetchAssetsForContext({
          pageOffset: 0,
          numberOfResults: 100,
          searchKeyword: "",
          folderId: activeFolder?.id || null,
          assetType: activeFilter === "all" ? null : activeFilter,
        });
        setCachedAssets(response.items.filter(matchesWorkspaceAssetFilter));
      } catch (error) {
        console.error("Failed to load cached assets:", error);
        setCachedAssets([]);
      }
    };

    loadCachedAssets();
  }, [activeFilter, activeFolder?.id, currentWorkspaceId, fetchAssetsForContext, folderMutationTick, matchesWorkspaceAssetFilter]);

  // Fetch all assets (no folder filter) for search suggestions across the whole workspace
  useEffect(() => {
    const loadAllAssets = async () => {
      try {
        const response = await fetchAssetsForContext({
          pageOffset: 0,
          numberOfResults: 100,
          searchKeyword: "",
          folderId: null,
          assetType: null,
        });
        setAllCachedAssets(response.items.filter(matchesWorkspaceAssetFilter));
      } catch (error) {
        setAllCachedAssets([]);
      }
    };
    loadAllAssets();
  }, [currentWorkspaceId, fetchAssetsForContext, folderMutationTick, matchesWorkspaceAssetFilter]);

  const createFolderWithName = useCallback(
    async (name: string): Promise<Folder | null> => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        return null;
      }

      const response = await TrebbleClientAPI.getInstance().createAssetFolder({
        workspaceId: currentWorkspaceId || undefined,
        name: trimmedName,
        color: FOLDER_COLORS[(folders.length + folderPath.length) % FOLDER_COLORS.length],
        parentFolderId: activeFolder?.id || null,
      });

      const createdFolder = normalizeFolderListResponse(response)[0] || null;

      if (createdFolder) {
        setFolders((currentFolders) => [...currentFolders, createdFolder]);
        setFolderMutationTick((currentTick) => currentTick + 1);
      } else {
        await loadFolders();
      }
      await loadAllFolders();

      return createdFolder;
    },
    [activeFolder?.id, currentWorkspaceId, folders.length, loadFolders, loadAllFolders, normalizeFolderListResponse]
  );

  const assignAssetToFolder = useCallback(
    async (assetId: string, folderId?: string | null) => {
      const response = await TrebbleClientAPI.getInstance().assignAssetFolderItem({
        workspaceId: currentWorkspaceId || undefined,
        itemId: assetId,
        folderId: folderId || undefined,
      });

      const nextFolders = normalizeFolderListResponse(response);
      if (nextFolders.length > 0) {
        setFolders(nextFolders);
      } else {
        await loadFolders();
      }
      await loadAllFolders();

      setFolderMutationTick((currentTick) => currentTick + 1);
    },
    [currentWorkspaceId, loadFolders, loadAllFolders, normalizeFolderListResponse]
  );

  const openAsset = useCallback((asset: AssetCardData | undefined) => {
    if (!asset) {
      return;
    }

    const url = asset.url || asset.previewUrl || asset.audioUrl || asset.videoUrl;
    if (!url) {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const downloadAsset = useCallback((asset: AssetCardData | undefined) => {
    if (!asset) {
      return;
    }

    const url = asset.url || asset.previewUrl || asset.audioUrl || asset.videoUrl;
    if (!url) {
      return;
    }

    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = asset.name || "asset";
    downloadLink.target = "_blank";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }, []);

  const handleDeleteAsset = useCallback(
    async (asset: AssetCardData | undefined) => {
      if (!asset) {
        return;
      }

      const confirmed = await (Utils as unknown as UtilsModule).getInstance().showUserConfirmationDialog(
        "Delete asset",
        `Delete "${asset.name}"? This cannot be undone.`
      );
      if (!confirmed) {
        return;
      }

      try {
        await TrebbleClientAPI.getInstance().deleteAsset({
          workspaceId: currentWorkspaceId || undefined,
          assetId: asset.id,
          sourceType: asset.sourceType,
        });
        setFolderMutationTick((currentTick) => currentTick + 1);
        await loadFolders();
        window.alertSuccessMessage?.("Asset deleted.");
      } catch (error) {
        console.error("Failed to delete asset:", error);
        window.alertErrorMessage?.("Unable to delete this asset.");
      }
    },
    [currentWorkspaceId, loadFolders]
  );

  const handleMoveAssetToFolder = useCallback(
    (asset: AssetCardData | undefined) => {
      if (!asset) return;
      if (!canManageFolders) {
        window.alertErrorMessage?.("Only a workspace owner or admin can manage folders.");
        return;
      }
      setPendingMoveAsset(asset);
      const router = PersistentModels.getInstance().getRouter();
      router.showMoveToFolderPopup(null, {
        folders: allFolders,
        currentFolderId: asset.folderId || activeFolder?.id,
        onConfirm: async (folderId: string | null) => {
          try {
            await assignAssetToFolder(asset.id, folderId);
            window.alertSuccessMessage?.(
              folderId ? "Asset moved to folder." : "Asset removed from folder."
            );
          } catch (error) {
            console.error("Failed to move asset to folder:", error);
            window.alertErrorMessage?.("Unable to update the folder for this asset.");
          } finally {
            setPendingMoveAsset(null);
          }
        },
        onCancel: () => {
          setPendingMoveAsset(null);
        },
        ti18n: ti18n
      });
    },
    [canManageFolders, allFolders, activeFolder?.id, assignAssetToFolder]
  );

  const handleNewFolder = useCallback(() => {
    if (!canManageFolders) {
      window.alertErrorMessage?.("You do not have permission to create folders here.");
      return;
    }
    setFolderNameModalOpen(true);
  }, [canManageFolders]);

  const handleFolderNameConfirm = useCallback(
    async (name: string) => {
      setFolderNameModalOpen(false);
      try {
        await createFolderWithName(name);
      } catch (error) {
        console.error("Failed to create asset folder:", error);
        window.alertErrorMessage?.("Unable to create the folder.");
      }
    },
    [createFolderWithName]
  );

  const handleDeleteFolder = useCallback(
    async (folder: Folder) => {
      if (!canManageFolders) {
        window.alertErrorMessage?.("You do not have permission to delete folders.");
        return;
      }
      if (folder.itemCount > 0) {
        const folderMustBeEmptyMessage = t("FOLDER_MUST_BE_EMPTY_BEFORE_DELETE");
        if (window.alertUserErrorMessage) {
          window.alertUserErrorMessage(folderMustBeEmptyMessage);
        } else if (window.alertWarningMessage) {
          window.alertWarningMessage(folderMustBeEmptyMessage);
        } else {
          window.alert(folderMustBeEmptyMessage);
        }
        return;
      }
      const confirmed = window.confirm(`Delete folder '${folder.name}'?`);
      if (!confirmed) return;
      try {
        const api = TrebbleClientAPI.getInstance();
        await api.deleteAssetFolder({
          folderId: folder.id,
          workspaceId: currentWorkspaceId || undefined,
        });
        await loadFolders();
        await loadAllFolders();
        setFolderMutationTick((currentTick) => currentTick + 1);
        // Silent success — no popup for folder operations
      } catch (error) {
        console.error("Failed to delete folder:", error);
        window.alertErrorMessage?.("Unable to delete the folder.");
      }
    },
    [canManageFolders, currentWorkspaceId, loadFolders, loadAllFolders]
  );

  const handleRenameFolder = useCallback((folder: Folder) => {
    if (!canManageFolders) {
      window.alertErrorMessage?.("You do not have permission to rename folders.");
      return;
    }
    setPendingRenameFolder(folder);
    setRenameFolderModalOpen(true);
  }, [canManageFolders]);

  const handleRenameFolderConfirm = useCallback(async (newName: string) => {
    setRenameFolderModalOpen(false);
    if (!pendingRenameFolder) return;
    try {
      const api = TrebbleClientAPI.getInstance();
      await api.renameAssetFolder({
        folderId: pendingRenameFolder.id,
        name: newName,
        workspaceId: currentWorkspaceId || undefined,
      });
      await loadFolders();
      await loadAllFolders();
      setFolderMutationTick((currentTick) => currentTick + 1);
      // Silent success — no popup for folder operations
    } catch (error) {
      console.error("Failed to rename folder:", error);
      window.alertErrorMessage?.("Unable to rename the folder.");
    } finally {
      setPendingRenameFolder(null);
    }
  }, [currentWorkspaceId, loadFolders, loadAllFolders, pendingRenameFolder]);

  const handleMoveFolderToFolder = useCallback((folder: Folder) => {
    if (!canManageFolders) {
      window.alertErrorMessage?.("You do not have permission to move folders.");
      return;
    }
    setPendingMoveFolder(folder);
    const excludedIds = getFolderAndDescendantIds(folder.id, allFolders);
    const filteredFolders = allFolders.filter((f) => !excludedIds.has(f.id));
    const router = PersistentModels.getInstance().getRouter();
    router.showMoveToFolderPopup(null, {
      folders: filteredFolders,
      currentFolderId: undefined,
      onConfirm: async (targetFolderId: string | null) => {
        try {
          const api = TrebbleClientAPI.getInstance();
          await api.moveAssetFolder({
            folderId: folder.id,
            parentFolderId: targetFolderId || undefined,
            workspaceId: currentWorkspaceId || undefined,
          });
          await loadFolders();
          await loadAllFolders();
          setFolderMutationTick((currentTick) => currentTick + 1);
        } catch (error) {
          console.error("Failed to move folder:", error);
          window.alertErrorMessage?.("Unable to move the folder.");
        } finally {
          setPendingMoveFolder(null);
        }
      },
      onCancel: () => {
        setPendingMoveFolder(null);
      },
      ti18n: ti18n
    });
  }, [canManageFolders, allFolders, currentWorkspaceId, loadFolders, loadAllFolders]);

  const handleAssetOpen = useCallback(
    (assetId: string) => {
      openAsset(assetLookup.get(assetId));
    },
    [assetLookup, openAsset]
  );

  const handleAssetDownload = useCallback(
    (assetId: string) => {
      downloadAsset(assetLookup.get(assetId));
    },
    [assetLookup, downloadAsset]
  );

  const handleAssetMenuAction = useCallback(
    (assetId: string, action: string) => {
      const asset = assetLookup.get(assetId);

      if (action === "move") {
        handleMoveAssetToFolder(asset);
        return;
      }

      if (action === "delete") {
        handleDeleteAsset(asset);
      }
    },
    [assetLookup, handleDeleteAsset, handleMoveAssetToFolder]
  );

  const fetchLibraryData = useCallback(
    async (pageOffset: number, pageSize: number) => {
      const assetType = activeFilter === "all" ? null : activeFilter;
      const folderId = activeFolder?.id || null;
      const needsWorkspaceOwnerFilter = !!currentWorkspaceId && workspaceAssetFilter === WORKSPACE_ASSET_FILTER_OWNED;

      if (activeSort === "newest" && !needsWorkspaceOwnerFilter) {
        const response = await fetchAssetsForContext({
          pageOffset,
          numberOfResults: pageSize,
          searchKeyword: appliedSearch,
          folderId,
          assetType,
        });

        return {
          items: response.items,
          hasMore: response.hasMore,
        };
      }

      const startIndex = pageOffset * pageSize;
      const endIndex = startIndex + pageSize;
      const fetchLimit = Math.max(100, (pageOffset + 1) * pageSize * 3);
      const filteredItems: AssetCardData[] = [];
      let backendPageOffset = 0;
      let backendHasMore = true;
      let sortedItems: AssetCardData[] = [];

      while (backendHasMore) {
        const response = await fetchAssetsForContext({
          pageOffset: backendPageOffset,
          numberOfResults: fetchLimit,
          searchKeyword: appliedSearch,
          folderId,
          assetType,
        });

        filteredItems.push(...response.items.filter(matchesWorkspaceAssetFilter));
        sortedItems = sortAssets(filteredItems, activeSort);
        backendHasMore = response.hasMore && response.items.length > 0;

        if (sortedItems.length >= endIndex || !backendHasMore) {
          break;
        }

        backendPageOffset += 1;
      }

      return {
        items: sortedItems.slice(startIndex, endIndex),
        hasMore: endIndex < sortedItems.length || backendHasMore,
      };
    },
    [activeFilter, activeFolder?.id, activeSort, appliedSearch, currentWorkspaceId, fetchAssetsForContext, matchesWorkspaceAssetFilter, workspaceAssetFilter]
  );

  const searchSuggestions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }

    const results: Array<{
      id: string;
      name: string;
      type: "folder" | AssetType;
      folderName?: string;
      folderColor?: string;
      itemCount?: number;
    }> = [];

    // Search across ALL folders workspace-wide
    allFolders.forEach((folder) => {
      if (folder.name.toLowerCase().includes(normalizedQuery)) {
        results.push({
          id: folder.id,
          name: folder.name,
          type: "folder",
          folderColor: folder.color,
          itemCount: folder.itemCount,
        });
      }
    });

    // Search across ALL assets workspace-wide
    let assetCount = 0;
    allCachedAssets.forEach((asset) => {
      if (assetCount >= 5 || !asset.name.toLowerCase().includes(normalizedQuery)) {
        return;
      }

      const folder = folderByAssetId.get(asset.id) || (asset.folderId ? allFolders.find((item) => item.id === asset.folderId) : undefined);
      results.push({
        id: asset.id,
        name: asset.name,
        type: asset.type,
        folderName: folder?.name,
        folderColor: folder?.color,
      });
      assetCount += 1;
    });

    return results;
  }, [allCachedAssets, allFolders, folderByAssetId, searchQuery]);

  const handleSuggestionClick = useCallback(
    (suggestion: { id: string; type: string }) => {
      if (suggestion.type === "folder") {
        const folder = allFolders.find((item) => item.id === suggestion.id) || folders.find((item) => item.id === suggestion.id) || null;
        if (folder) {
          setActiveFolder(folder);
          // Reconstruct full ancestor path from allFolders
          const buildAncestorPath = (f: Folder): Folder[] => {
            if (!f.parentFolderId) return [f];
            const parent = allFolders.find(p => p.id === f.parentFolderId);
            return parent ? [...buildAncestorPath(parent), f] : [f];
          };
          setFolderPath(buildAncestorPath(folder));
        }
        setSearchQuery("");
        setAppliedSearch("");
        setIsSearchResultsView(false);
        setSearchResults([]);
        return;
      }

      handleAssetOpen(suggestion.id);
    },
    [allFolders, folders, handleAssetOpen]
  );

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    // Only update suggestion popup — don't filter the page content.
    // Page filtering happens on explicit submit (Enter / click "Search for…").
    if (!query.trim()) {
      setAppliedSearch("");
      setIsSearchResultsView(false);
      setSearchResults([]);
    }
  }, []);

  const handleSearchSubmit = useCallback(async () => {
    const normalizedQuery = searchQuery.trim();
    if (!normalizedQuery) {
      return;
    }

    setAppliedSearch(normalizedQuery);
    setSearchLoading(true);
    setIsSearchResultsView(true);

    try {
      const response = await fetchAssetsForContext({
        pageOffset: 0,
        numberOfResults: 250,
        searchKeyword: normalizedQuery,
        folderId: null, // Search across entire workspace, not just current folder
        assetType: activeFilter === "all" ? null : activeFilter,
      });

      setSearchResults(sortAssets(response.items.filter(matchesWorkspaceAssetFilter), activeSort));
    } catch (error) {
      console.error("Asset search failed:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [activeFilter, activeFolder?.id, activeSort, fetchAssetsForContext, matchesWorkspaceAssetFilter, searchQuery]);

  const handleCloseSearch = useCallback(() => {
    setSearchQuery("");
    setAppliedSearch("");
    setSearchResults([]);
    setIsSearchResultsView(false);
  }, []);

  const handleSearchLocationClick = useCallback((folderId: string | null) => {
    if (!folderId) {
      setActiveFolder(null);
      setFolderPath([]);
    } else {
      const folder = allFolders.find((item) => item.id === folderId) || folders.find((item) => item.id === folderId);
      if (!folder) {
        return;
      }
      setActiveFolder(folder);
      const buildAncestorPath = (f: Folder): Folder[] => {
        if (!f.parentFolderId) return [f];
        const parent = allFolders.find(p => p.id === f.parentFolderId);
        return parent ? [...buildAncestorPath(parent), f] : [f];
      };
      setFolderPath(buildAncestorPath(folder));
    }

    setSearchQuery("");
    setAppliedSearch("");
    setSearchResults([]);
    setIsSearchResultsView(false);
  }, [allFolders, folders]);

  const filterOptions = useMemo(
    () => [
      { key: "all", label: t("ALL") },
      { key: "audio", label: t("AUDIO") },
      { key: "video", label: t("VIDEO") },
      { key: "image", label: t("IMAGES") },
      { key: "vector", label: t("VECTORS") },
    ],
    []
  );

  return (<>
    <div
      style={{
        width: "100%",
        color: "var(--text-default-color)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 32px" }}>
        <div style={{ padding: "12px 0 0" }}>
          <LibrarySearchBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            onFilterClick={() => setFilterOpen(!filterOpen)}
            placeholder={t("SEARCH")}
            ti18n={ti18n}
            suggestions={searchSuggestions}
            showSuggestions={searchSuggestions.length > 0}
            onSuggestionClick={handleSuggestionClick}
            itemSectionLabel={t("ASSETS")}
            filterElement={
              <LibraryFilterPopup
                open={filterOpen}
                onOpenChange={setFilterOpen}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                activeSort={activeSort}
                onSortChange={setActiveSort}
                ti18n={ti18n}
                typeOptions={filterOptions}
                sortOptions={SORT_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }))}
              >
                <button
                  type="button"
                  onClick={() => setFilterOpen(!filterOpen)}
                  aria-label="Open filters"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 4,
                    margin: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                  }}
                >
                  <span style={{ position: "relative", display: "inline-flex", overflow: "visible" }}>
                    <i
                      className="pe-7s-filter"
                      style={{
                        WebkitTextStroke: "0.5px",
                        fontSize: "var(--text-lg)",
                        color:
                          filterOpen || activeFilter !== "all" || activeSort !== "newest"
                            ? "var(--trebble-primary, #EC7F54)"
                            : "var(--text-muted)",
                        lineHeight: 1,
                      }}
                    />
                    {(activeFilter !== "all" || activeSort !== "newest") && (
                      <span style={{ position: "absolute", top: 0, right: 0, width: 8, height: 8, backgroundColor: "var(--trebble-primary, #EC7F54)", borderRadius: "50%", transform: "translate(50%, -50%)" }} />
                    )}
                  </span>
                </button>
              </LibraryFilterPopup>
            }
          />
        </div>

        {isSearchResultsView ? (
          <LibrarySearchResults
            query={searchQuery}
            results={searchResults.map((asset) => {
              const folder = folderByAssetId.get(asset.id) || (asset.folderId ? allFolders.find((item) => item.id === asset.folderId) : undefined);
              return {
                id: asset.id,
                name: asset.name,
                type: asset.type,
                date: asset.date,
                duration: asset.duration,
                folderId: folder?.id || null,
                folderName: folder?.name || rootLabel,
                folderColor: folder?.color,
              };
            })}
            onClose={handleCloseSearch}
            onResultClick={handleAssetOpen}
            onLocationClick={handleSearchLocationClick}
            loading={searchLoading}
            ti18n={ti18n}
          />
        ) : (
          <>
            <LibraryToolbar
              activeFolder={activeFolder}
              folderPath={folderPath}
              onNavigateRoot={() => { setActiveFolder(null); setFolderPath([]); }}
              onNavigateToFolder={(folder) => {
                setFolderPath(prev => {
                  const idx = prev.findIndex(f => f.id === folder.id);
                  return idx >= 0 ? prev.slice(0, idx + 1) : prev;
                });
                setActiveFolder(folder);
              }}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              ti18n={ti18n}
              rootLabel={rootLabel}
            />

            {currentWorkspaceId && (
              <div style={{ margin: "0 0 24px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                <TrebbleFilterButtons
                  filters={[
                    { key: WORKSPACE_ASSET_FILTER_ALL, label: t("ALL_MEDIA_TAB") },
                    { key: WORKSPACE_ASSET_FILTER_OWNED, label: t("ADDED_BY_ME_TAB") },
                  ]}
                  activeFilter={workspaceAssetFilter}
                  onFilterChange={setWorkspaceAssetFilter}
                  useEditorTheme={false}
                />
              </div>
            )}

            {(folders.length > 0 || canManageFolders) && (
              <div style={{ marginBottom: 24 }}>
                <h3 className="library-section-label">{t("FOLDERS")}</h3>
                <FolderRow
                  folders={folders}
                  onFolderClick={(folderId: string) => {
                    const selectedFolder = folders.find((folder) => folder.id === folderId) || null;
                    if (selectedFolder) {
                      setFolderPath(prev => [...prev, selectedFolder]);
                    } else {
                      setFolderPath([]);
                    }
                    setActiveFolder(selectedFolder);
                  }}
                  onNewFolder={handleNewFolder}
                  onDeleteFolder={handleDeleteFolder}
                  onRenameFolder={handleRenameFolder}
                  onMoveFolder={handleMoveFolderToFolder}
                  canCreateFolder={canManageFolders}
                  ti18n={ti18n}
                  hideItemCount={workspaceAssetFilter === WORKSPACE_ASSET_FILTER_OWNED || activeFilter !== "all"}
                />
              </div>
            )}

            <div>
              <h3 className="library-section-label">
                {!currentWorkspaceId
                  ? t("ASSETS")
                  : workspaceAssetFilter === WORKSPACE_ASSET_FILTER_OWNED
                    ? t("ADDED_BY_ME_TAB")
                    : t("ALL_MEDIA_TAB")}
              </h3>
              <TrebbleInfiniteList
                key={`assets-${currentWorkspaceId || "personal"}-${workspaceAssetFilter}-${activeFolder?.id || "root"}-${folderMutationTick}-${viewMode}-${appliedSearch}-${activeFilter}-${activeSort}`}
                containerId="assetsInfiniteScroll"
                containerClassname={viewMode === "grid" ? "library-infinite-grid" : "library-infinite-list"}
                containerHeightMargin={320}
                pageSize={PAGE_SIZE}
                fetchData={fetchLibraryData}
                loader={<TrebbleSkeleton useEditorTheme={false} active paragraph={{ rows: 3 }} />}
                locale={{ emptyText: <EmptyState message={currentWorkspaceId && workspaceAssetFilter === WORKSPACE_ASSET_FILTER_OWNED ? t("NO_ADDED_BY_ME_ASSETS_FOUND") : t("NO_ASSETS_FOUND")} /> }}
                renderItem={(item: AssetCardData) => {
                  const menuActions: Array<"move" | "delete"> = [];

                  if (canManageFolders) {
                    menuActions.push("move");
                  }

                  if (item.ownerUserUID && item.ownerUserUID === currentUserId) {
                    menuActions.push("delete");
                  }

                  return (
                    <ProjectCard
                      key={item.id}
                      id={item.id}
                      name={item.name}
                      type={item.type}
                      audioUrl={item.audioUrl}
                      videoUrl={item.videoUrl}
                      previewUrl={item.previewUrl}
                      duration={item.duration}
                      date={item.date}
                      creator={item.creator}
                      viewMode={viewMode}
                      ti18n={ti18n}
                      primaryActionLabel={t("OPEN")}
                      primaryActionIconClassName="pe-7s-right-arrow"
                      menuActions={menuActions}
                      onPlay={handleAssetOpen}
                      onDownload={handleAssetDownload}
                      onClick={handleAssetOpen}
                      onMenuAction={handleAssetMenuAction}
                    />
                  );
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
    <FolderNameModal
      open={folderNameModalOpen}
      onConfirm={handleFolderNameConfirm}
      onCancel={() => setFolderNameModalOpen(false)}
      ti18n={ti18n}
    />
    <FolderNameModal
      open={renameFolderModalOpen}
      title={t("FOLDER_RENAME_FOLDER")}
      okText={t("FOLDER_RENAME")}
      initialValue={pendingRenameFolder?.name || ""}
      onConfirm={handleRenameFolderConfirm}
      onCancel={() => { setRenameFolderModalOpen(false); setPendingRenameFolder(null); }}
      ti18n={ti18n}
    /></>
  );
};

export default AssetsLibraryPage;
